import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MenuItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { CesPopupMenuComponent } from '../../../../ui/ces-popup-menu/ces-popup-menu.component';
import { TableLazyLoadEvent } from 'primeng/table';
import { filter, Subject, takeUntil } from 'rxjs';
import { assetIdSignalFromRoute } from '../../../../../utils/asset-route.utils';
import { assetWriteSignal } from '../../../../../utils/asset-permissions.utils';
import { HasPermissionDirective } from '../../../../../directives/has-permission.directive';
import { graphDefinition } from '../../../../../entity-definitions/graph.definition';
import { GraphicService } from '../../../../../services/api/graphic.service';
import { PaginatedUrlSyncService, ParsedFilters } from '../../../../../services/paginated-url-sync.service';
import { ConfirmationHelperService } from '../../../../../services/confirmation-helper.service';
import { ModalHelperService } from '../../../../../services/modal-helper.service';
import { GraphStore } from '../../../../../stores/graph.store';
import { EntityDefinition } from '../../../../../types/entity-definition.interface';
import { Graph } from '../../../../../types/graph.interface';
import { MenuItemWithIcon } from '../../../../../types/menu.interface';
import { PermissionScope } from '../../../../../types/permission-scope.enum';
import { buildEntityMenuItems } from '../../../../../utils/entity-menu.utils';
import { LazyLoadConverter } from '../../../../../utils/lazy-load.converter';
import { GenericParentGridComponent } from '../../../../generic/generic-parent-grid.component';
import { CesAssetBreadcrumbComponent } from '../../../../ui/ces-asset-breadcrumb/ces-asset-breadcrumb.component';
import { CesButtonComponent } from '../../../../ui/ces-buttons/ces-button.component';
import { CesTitleComponent } from '../../../../ui/ces-route-title/ces-route-title.component';
import { CreateGraphDialogComponent, CreateGraphDialogResult } from './create-graph-dialog.component';

@Component({
  selector: 'ces-graphs-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DialogService],
  imports: [
    CesAssetBreadcrumbComponent,
    CesButtonComponent,
    CesTitleComponent,
    GenericParentGridComponent,
    CesPopupMenuComponent,
    HasPermissionDirective,
    TranslocoModule,
  ],
  templateUrl: './graphs-list.component.html',
})
export class CesGraphsListComponent implements OnInit, OnDestroy {
  protected readonly PermissionScope = PermissionScope;

  readonly #destroy$ = new Subject<void>();
  #isLoadingFromGrid = false;
  #isInitialLoad = true;

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly graphStore = inject(GraphStore);
  private readonly graphApiService = inject(GraphicService);
  private readonly dialogService = inject(DialogService);
  private readonly confirmationHelper = inject(ConfirmationHelperService);
  private readonly modalHelper = inject(ModalHelperService);
  private readonly translateService = inject(TranslocoService);
  private readonly paginatedUrlSync = inject(PaginatedUrlSyncService);
  private readonly translations = toSignal(this.translateService.selectTranslation());

  readonly actionMenu = viewChild.required<CesPopupMenuComponent>('actionMenu');
  readonly selectedGraph = signal<Graph | null>(null);
  readonly isDeleting = signal(false);

  readonly currentAssetId = assetIdSignalFromRoute(this.route);
  private readonly canWriteAsset = assetWriteSignal(PermissionScope.Graphics, this.currentAssetId);

  readonly entityDefinition = computed((): EntityDefinition<Graph> => {
    const assetId = this.currentAssetId() ?? undefined;
    const customActions = graphDefinition.actionButtons?.parent?.custom?.map(action =>
      action.hasPermission ? { ...action, hasPermission: { ...action.hasPermission, assetId } } : action,
    );
    return {
      ...graphDefinition,
      actionButtons: {
        ...graphDefinition.actionButtons,
        parent: {
          ...graphDefinition.actionButtons?.parent,
          ...(customActions ? { custom: customActions } : {}),
        },
      },
      rowLinkHref: graph => (graph.assetId ? ['/assets', graph.assetId, 'analysis', 'graphs', graph.id] : null),
    };
  });
  readonly urlFilters = signal<ParsedFilters | undefined>(
    this.paginatedUrlSync.parseFiltersFromUrl(this.route.snapshot.queryParams, this.entityDefinition()),
  );

  readonly graphs = computed(() => {
    const assetId = this.currentAssetId();
    return assetId ? this.graphStore.graphsByAssetId(assetId) : [];
  });

  private readonly existingGraphNames = computed(() => this.graphs().map((g: Graph) => g.name));

  readonly totalRecords = computed(() => this.graphStore.paginationState().totalItems);
  readonly first = computed(() => {
    const page = this.graphStore.paginationState().currentPage;
    const limit = this.graphStore.paginationState().limit;
    return (page - 1) * limit;
  });
  readonly rows = computed(() => this.graphStore.paginationState().limit);

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        filter(event => event.url.includes('/analysis/graphs') && !event.url.match(/\/analysis\/graphs\/\d+$/)),
        takeUntil(this.#destroy$),
      )
      .subscribe(() => {
        if (this.#isLoadingFromGrid) return;
        const assetId = this.currentAssetId();
        if (assetId) {
          void this.#loadGraphsFromUrl(assetId);
        }
      });

    const assetId = this.currentAssetId();
    if (assetId) {
      void this.#loadGraphsFromUrl(assetId);
    }
  }

  async #loadGraphsFromUrl(assetId: number): Promise<void> {
    const { queryParams, toSync } = this.paginatedUrlSync.resolveState(
      this.route,
      this.paginatedUrlSync.buildStateKey('graphs-grid', assetId),
    );
    const tableEvent = this.paginatedUrlSync.buildTableEventFromUrl(queryParams, this.entityDefinition());
    this.urlFilters.set(tableEvent.filters);

    if (toSync) {
      this.#isLoadingFromGrid = true;
      this.paginatedUrlSync.syncUrlInBackground(this.router, this.route, toSync, () => (this.#isLoadingFromGrid = false));
    }

    const params = LazyLoadConverter.fromTableEvent(tableEvent, ['name', 'description', 'measures']);
    await this.graphStore.loadPaginatedByAssetId(assetId, params);
  }

  readonly breadcrumbTrail = computed((): MenuItem[] => {
    this.translations();
    return [{ label: this.translateService.translate('graphs.breadcrumb') }];
  });

  readonly menuItems = computed((): MenuItemWithIcon[] => {
    this.translations();
    return buildEntityMenuItems(
      this.translateService,
      'graphs.actions',
      {
        onEdit: () => this.onEditChart(),
        onDuplicate: () => this.onDuplicateChart(),
        onDelete: () => {
          void this.onDeleteChart();
        },
      },
      { canWrite: this.canWriteAsset() },
    );
  });

  onRowAction(action: string, graph: Graph, event?: MouseEvent): void {
    if (action === 'menu' && event) {
      this.selectedGraph.set(graph);
      this.actionMenu().toggle(event);
    }
  }

  async onLazyLoad(event: TableLazyLoadEvent): Promise<void> {
    const assetId = this.currentAssetId();
    if (!assetId) return;

    if (this.#isInitialLoad) {
      this.#isInitialLoad = false;
      return;
    }

    this.#isLoadingFromGrid = true;

    try {
      const queryParams = this.paginatedUrlSync.buildQueryParamsFromEvent(event, this.entityDefinition());
      this.paginatedUrlSync.saveState(this.paginatedUrlSync.buildStateKey('graphs-grid', assetId), queryParams);
      await this.paginatedUrlSync.navigateWithParams(this.router, this.route, queryParams);

      const params = LazyLoadConverter.fromTableEvent(event, ['name', 'description', 'measures']);
      await this.graphStore.loadPaginatedByAssetId(assetId, params);
    } finally {
      this.isDeleting.set(false);
      this.#isLoadingFromGrid = false;
    }
  }

  private onEditChart(): void {
    const graph = this.selectedGraph();
    if (!graph || this.isDeleting()) return;

    this.isDeleting.set(true);

    this.dialogService
      .open(
        CreateGraphDialogComponent,
        this.modalHelper.getFormConfig({
          header: `${this.translateService.translate('graphs.actions.edit')} "${graph.name}"`,
          data: { mode: 'edit', graph, existingGraphNames: this.existingGraphNames() },
        }),
      )
      ?.onClose.subscribe((result: CreateGraphDialogResult | undefined) => {
        void this.handleEditResult(graph, result);
      });
  }

  private async handleEditResult(graph: Graph, result: CreateGraphDialogResult | undefined): Promise<void> {
    try {
      if (result) {
        await this.graphStore.update(graph.id, result);
        this.graphStore.invalidateCache();

        const assetId = this.currentAssetId();
        if (assetId) {
          void this.#loadGraphsFromUrl(assetId);
        }
      }
    } finally {
      this.isDeleting.set(false);
    }
  }

  private onDuplicateChart(): void {
    const graph = this.selectedGraph();
    if (!graph || this.isDeleting()) return;

    this.isDeleting.set(true);

    this.dialogService
      .open(
        CreateGraphDialogComponent,
        this.modalHelper.getFormConfig({
          header: `${this.translateService.translate('graphs.actions.duplicate')} "${graph.name}"`,
          data: { mode: 'duplicate', graph, existingGraphNames: this.existingGraphNames() },
        }),
      )
      ?.onClose.subscribe((result: CreateGraphDialogResult | undefined) => {
        void this.handleDuplicateResult(graph, result);
      });
  }

  private async handleDuplicateResult(graph: Graph, result: CreateGraphDialogResult | undefined): Promise<void> {
    try {
      if (!result) return;

      const assetId = this.currentAssetId();
      if (!assetId) return;

      const createdGraph = await this.graphApiService.duplicateGraphAsync(graph.id, {
        name: result.name,
        shortName: result.shortName,
        description: result.description,
      });

      this.graphStore.invalidateCache();
      void this.router.navigate(['/assets', assetId, 'analysis', 'graphs', createdGraph.id]);
    } finally {
      this.isDeleting.set(false);
    }
  }

  private async onDeleteChart(): Promise<void> {
    const graph = this.selectedGraph();
    if (!graph || this.isDeleting()) return;
    this.isDeleting.set(true);
    try {
      await this.confirmationHelper.confirmAndDelete({
        entityName: graph.name,
        messageKey: 'graphs.confirmDeleteMessage',
        onDelete: () => this.graphStore.delete(graph.id),
        onSuccess: () => {
          this.graphStore.invalidateCache();
          const assetId = this.currentAssetId();
          if (assetId) void this.#loadGraphsFromUrl(assetId);
        },
      });
    } finally {
      this.isDeleting.set(false);
    }
  }

  onCreateGraph(): void {
    this.dialogService
      .open(
        CreateGraphDialogComponent,
        this.modalHelper.getFormConfig({
          header: this.translateService.translate('graphs.createChart'),
          data: { mode: 'create', existingGraphNames: this.existingGraphNames() },
        }),
      )
      ?.onClose.subscribe((result: CreateGraphDialogResult | undefined) => {
        void this.handleCreateResult(result);
      });
  }

  private async handleCreateResult(result: CreateGraphDialogResult | undefined): Promise<void> {
    if (!result) return;
    const assetId = this.currentAssetId();
    if (!assetId) return;

    const createdGraph = await this.graphStore.create({ ...result, assetId });
    if (!createdGraph) return;
    this.graphStore.invalidateCache();
    void this.router.navigate(['/assets', assetId, 'analysis', 'graphs', createdGraph.id]);
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
